<template>
  <div v-bind:key="refreshKey">

    <b-table responsive
             v-handy-scroll
             v-bind:items="psps"
             v-bind:fields="fields"
             no-local-sorting
             v-bind:sort-by.sync="sort.field"
             v-bind:sort-desc.sync="sortIsDesc"
             striped
             hover
             @sort-changed="onSortChanged"
             @row-clicked="onRowClicked"
             style="white-space: nowrap; padding-bottom: 30px"
    >

      <template #cell(name)="data">
        <span class="font-weight-bold">{{ data.value }}</span>
      </template>

      <template #cell(sortOrder)="data">
        <span v-if="data.value != null">{{ data.value }}</span>
        <span v-else class="text-muted">—</span>
      </template>

      <template #cell(active)="data">
        <b-badge v-if="data.value !== false" variant="light-success">{{ $t('operations.yes') }}</b-badge>
        <b-badge v-else variant="light-danger">{{ $t('operations.no') }}</b-badge>
      </template>

      <template #cell(creationTime)="data">
        {{ data.value | momentDateTime }}
      </template>

      <template #cell(actions)="row">
        <div class="btn-container" v-if="hasPermission(permissions.PSP_MANAGE)">
          <b-button size="sm" variant="success" v-b-tooltip v-bind:title="$t('operations.edit')"
                    @click.stop="onEdit(row.item)"
          >
            <i class="fas fa-pencil-alt"></i>
          </b-button>
          <b-button size="sm" variant="danger" v-b-tooltip v-bind:title="$t('operations.delete')"
                    @click.stop="onDelete(row.item)"
          >
            <i class="fas fa-trash"></i>
          </b-button>
        </div>
      </template>

    </b-table>

    <div class="mt-2 text-center">
      <div class="d-inline-block">
        <b-pagination
            v-model="pagination.page"
            v-bind:total-rows="pagination.total"
            v-bind:per-page="pagination.size"
            @page-click="onPageClick"
            align="center"
        ></b-pagination>
      </div>
      <div class="d-inline-block">
        <b-select class="d-inline-block" style="width: 70px" size="sm" v-model="pagination.size" @change="refresh">

          <b-select-option v-bind:value="25">25</b-select-option>
          <b-select-option v-bind:value="50">50</b-select-option>
          <b-select-option v-bind:value="100">100</b-select-option>
        </b-select>
        /{{ pagination.total }} {{ $t('columns.counterNames.psps') }}

      </div>

    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import mixinTable from '@/components/mixin/mixinTable';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinBase from '@/components/mixin/mixinBase';
import mixinPermissions from '@/components/mixin/mixinPermissions';

export default {
  name: 'PspTable',
  components: {},
  mixins: [mixinBase, mixinTable, mixinNotifications, mixinPermissions],
  data: () => ({
    psps: [],
  }),
  props: {

    filters: {
      default: () => ({})
    },
    columns: {
      default: () => ([])
    },
    lazy: {
      default: () => false
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.pagination.page = 1;
        this.refresh();
      }
    }
  },
  created() {
    this.setPagination(1, 0, 25);
    this.refresh();

  },
  computed: {
    fields() {
      return [
        {
          key: 'id',
          label: this.$t('columns.id'),
          sortable: true,
        },
        {
          key: 'name',
          label: this.$t('columns.name'),
          sortable: true,
        },
        {
          key: 'sortOrder',
          label: this.$t('columns.sortOrder'),
          sortable: true,
        },
        {
          key: 'active',
          label: this.$t('columns.active'),
          sortable: false,
        },
        {
          key: 'creationTime',
          label: this.$t('columns.creationTime'),
          sortable: true,
        },
        {
          key: 'actions',
          label: this.$t('columns.actions'),
          class: 'text-center',
          sortable: false,
        },
      ];
    },

  },
  methods: {
    ...mapActions('psp', ['getAllPsps', 'deletePsp']),
    onEdit(psp) {
      this.$emit('selected', psp.id);
    },
    onDelete(psp) {
      let $this = this;
      this.$bvModal.msgBoxConfirm($this.$t('operations.delete') + ' "' + psp.name + '"?', {
        title: $this.$t('globalMessages.pleaseConfirm'),
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: $this.$t('operations.yes'),
        cancelTitle: $this.$t('operations.no'),
        footerClass: 'p-2',
        centered: true,
      })
          .then(value => {
            if (!value) return;
            $this.deletePsp(psp.id)
                .then(() => {
                  $this.showSuccessNotification($this.$t('globalMessages.deleteDataSuccess', { system: psp.name }));
                  $this.refresh();
                })
                .catch(err => {
                  $this.showErrorNotification($this.$t('globalMessages.deleteDataError', { system: psp.name }), $this.getErrorDescription(err));
                });
          });
    },

    async refresh() {
      let $this = this;
      let loadingToken = this.setLoading();
      let sort = { ...$this.sort };
      if (sort.field == null) {
        sort.field = 'creationTime';
        sort.direction = 'DESC';
      }

      const result = await this.getAllPsps(
          {
            pagination: this.pagination,
            sort,
            filter: this.filters,
          }
      )
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.psps') }), $this.getErrorDescription(err));
            $this.setLoaded(loadingToken);
          });
      if (result) {
        this.psps = result.data;
        this.setMetaDataFromResult(result);
      }
      this.setLoaded(loadingToken);
    }
  }
};
</script>

<style scoped>
.btn-container .btn {
  margin-right: 2px;
}
</style>
